import {
  HandoffContinuePage,
  RequireModule,
  RequirePermission,
  RequireSession,
  RequireTenant,
  RequireTenantMfa,
  RouteErrorPage,
  SecurityPage,
  SignInPage,
  TenantRedirect,
  churchUrl,
  useMyMemberships,
  useOnStaffDoor,
  useStartupChecks,
  useTenant,
} from "@how/app-kit";
import { Suspense, lazy, useEffect } from "react";
import { Navigate, createBrowserRouter, type RouteObject } from "react-router";
import { EmptyState, ErrorState, LoadingState } from "@how/ui";
import { RenamedPath } from "./RenamedPath";
import { RENAMED_PATHS } from "./renamed-paths";
import { HomePage } from "../features/home/HomePage";
import { DuplicatesPage } from "../features/people/duplicates/DuplicatesPage";
import { FormEditorPage } from "../features/website/FormEditorPage";
import { FormsPage } from "../features/website/FormsPage";
import { SermonsPage } from "../features/website/SermonsPage";
import { DevotionalsPage } from "../features/website/DevotionalsPage";
import { ConsentPage } from "../features/people/ConsentPage";
import { CampusesPage } from "../features/campuses/CampusesPage";
import { DoorsPage } from "../features/check-in/DoorsPage";
import { RoomsPage } from "../features/check-in/RoomsPage";
import { StationPage } from "../features/check-in/StationPage";
import { EventsPage } from "../features/events/EventsPage";
import { GroupsPage } from "../features/groups/GroupsPage";
import { RequireAnyPermission } from "../features/groups/RequireAnyPermission";
import { ServicesPage } from "../features/services/ServicesPage";
import { MembershipPage } from "../features/membership/MembershipPage";
import { AudiencesPage } from "../features/communications/AudiencesPage";
import { OrdersPage } from "../features/store/OrdersPage";
import { ProductsPage } from "../features/store/ProductsPage";
import { StoreSettingsPage } from "../features/store/StoreSettingsPage";
import { CampaignsPage } from "../features/communications/CampaignsPage";
import { InboxPage } from "../features/communications/InboxPage";
import { TextUsPage } from "../features/communications/TextUsPage";
import { SendersPage } from "../features/communications/SendersPage";
import { TemplatesPage } from "../features/communications/TemplatesPage";
import { CarePage } from "../features/care/CarePage";
import { DomainPage } from "../features/website/DomainPage";
import { LookPage } from "../features/website/LookPage";
import { MediaPage } from "../features/website/MediaPage";
import { MenusPage } from "../features/website/MenusPage";
import { HouseholdsPage } from "../features/people/HouseholdsPage";
import { ImportPage } from "../features/people/import/ImportPage";
import { PeoplePage } from "../features/people/PeoplePage";
import { TagsPage } from "../features/people/TagsPage";
import { PageEditorPage } from "../features/website/PageEditorPage";
import { WebsitePagesPage } from "../features/website/WebsitePagesPage";
import { SettingsPage as WebsiteSettingsPage } from "../features/website/SettingsPage";
import { StudioVariant } from "../features/website/website-variant";
import { SettingsPage } from "../features/settings/SettingsPage";
import { SignInAddressesPage } from "../features/settings/SignInAddressesPage";
import { SupportAccessPage } from "../features/support/SupportAccessPage";
import { RolesPage } from "../features/team/RolesPage";
import { TeamPage } from "../features/team/TeamPage";
import { env } from "../lib/env";
import { NAV_CHECKS } from "./nav";

// Reports draws charts, and with them Recharts (about 105 kB gzipped): loaded when opened, not with every screen
// (docs/UI_STANDARDS.md, Charts).
const ReportsPage = lazy(async () => ({
  default: (await import("../features/reports/ReportsPage")).ReportsPage,
}));
import { DoorLayout } from "./DoorLayout";
import { doorLanding, staffChurchOf, staffUnavailableMessage } from "./door-landing";
import { MyScheduleMoved } from "./MyScheduleMoved";
import { StaffMembershipGuard } from "./RequireStaffAccess";
import { StaffOnlyPage } from "./StaffOnlyPage";
import { Shell } from "./Shell";
import { useMyUnavailableStaffChurches } from "./unavailable-staff-churches";

/**
 * The staff address with no church in the path (ADR 0068, ADR 0070 R12): a login opens its one staff church; one
 * whose staff church cannot be opened is told why (R16, P16); one with member churches only (volunteers included) is
 * shown its member portals, where My schedule lives; one with neither is told it has no staff access.
 */
export function DoorHome() {
  return (
    <RequireSession>
      <DoorLanding />
    </RequireSession>
  );
}

function DoorLanding() {
  const memberships = useMyMemberships();
  const onDoor = useOnStaffDoor();
  const staffChurch = memberships.data === undefined ? undefined : staffChurchOf(memberships.data);
  // Asked only when it can decide the landing: why a staff church cannot open, when there is no usable one (P16).
  const askUnavailable = memberships.data !== undefined && staffChurch === undefined;
  const unavailable = useMyUnavailableStaffChurches(askUnavailable);
  const landing = doorLanding(memberships.data, askUnavailable ? unavailable.data : undefined);
  const target = landing.kind === "church" ? landing.slug : null;
  useEffect(() => {
    // A full page load, as opening a church always is: nothing the door held survives into the church.
    if (target !== null) window.location.replace(churchUrl(target, "staff_dashboard", env, onDoor));
  }, [target, onDoor]);
  if (memberships.isError || (askUnavailable && unavailable.isError)) {
    return (
      <DoorLayout>
        <ErrorState
          message="Could not load your churches."
          onRetry={() => {
            void memberships.refetch();
            if (askUnavailable) void unavailable.refetch();
          }}
        />
      </DoorLayout>
    );
  }
  switch (landing.kind) {
    case "loading":
      return <LoadingState label="Opening HoW" />;
    case "church":
      return <LoadingState label={`Opening ${staffChurch?.name ?? "your church"}`} />;
    case "staff-unavailable":
      return (
        <DoorLayout>
          <StaffOnlyPage
            title="Your staff church cannot be opened"
            description={`${staffUnavailableMessage(landing.church)}${
              landing.churches.length > 0 ? " You can still open your member portal." : ""
            }`}
            churches={landing.churches}
          />
        </DoorLayout>
      );
    case "member-only":
      return (
        <DoorLayout>
          <StaffOnlyPage churches={landing.churches} />
        </DoorLayout>
      );
    case "no-access":
      return (
        <DoorLayout>
          <EmptyState
            title="You do not have staff access to any church yet"
            description="Ask your church for an invitation, then open the link in that email."
          />
        </DoorLayout>
      );
  }
}

/**
 * dash.<base> without a church is the staff door (it opens the person's church); with /<slug> in the path it is that
 * church.
 */
function HostSwitch() {
  const tenant = useTenant();
  // Every start-up check at once, the navigation's permissions included (#286); a no-op on the staff address with no
  // church in the path (the door), where there is no church to check.
  useStartupChecks(NAV_CHECKS);
  if (tenant.status === "central") return <DoorHome />;
  return (
    <RequireTenant>
      <RequireSession>
        <StaffMembershipGuard>
          <RequireTenantMfa>
            <Shell />
          </RequireTenantMfa>
        </StaffMembershipGuard>
      </RequireSession>
    </RequireTenant>
  );
}

/** The sidebar preview (2026-10-13): every church route again under /sidebar, where Shell draws the new sidebar. */
function withSidebarPreview(routes: RouteObject[]): RouteObject[] {
  return [...routes, { path: "sidebar", children: routes }];
}

/** basename is "/<slug>" on the staff door once a church is in the path, otherwise "". */
export function createAppRouter(basename: string) {
  return createBrowserRouter(
    [
      {
        errorElement: <RouteErrorPage />,
        path: "/sign-in",
        element: (
          <SignInPage
            title="HoW Staff Dashboard"
            subtitle="Church staff sign-in"
            footnote="Access is by invitation from your church's administrators."
            movedNotice="The staff dashboard has a new address. Sign in once more here."
          />
        ),
      },
      {
        errorElement: <RouteErrorPage />,
        // The hand-off lands here (spec section 6): no session yet, no church shell.
        path: "/auth/continue",
        element: <HandoffContinuePage env={env} />,
      },
      {
        errorElement: <RouteErrorPage />,
        path: "/t/:slug",
        element: <TenantRedirect env={env} surface="staff_dashboard" />,
      },
      {
        errorElement: <RouteErrorPage />,
        // Old Choose a church links (ADR 0068): the door's home opens the person's church. `choose` stays reserved.
        path: "/choose",
        element: <Navigate to="/" replace />,
      },
      // Old My schedule links (ADR 0070 R10): a door page, so only when no church is in the path. `me` stays reserved.
      ...(basename === ""
        ? [{ errorElement: <RouteErrorPage />, path: "/me/schedule", element: <MyScheduleMoved /> }]
        : []),
      {
        errorElement: <RouteErrorPage />,
        path: "/",
        element: <HostSwitch />,
        children: withSidebarPreview([
          { index: true, element: <HomePage /> },
          // Aurora, the same Home in the night-and-glass design (preview, restored 2026-10-13). The old /home2/1 to
          // /home2/4 addresses open it too.
          { path: "home2", element: <HomePage design="aurora" /> },
          { path: "home2/*", element: <Navigate to="/home2" replace /> },
          // The invitation email's link (invite-member). RequireMembership above has already offered the invitation
          // and accepted it; here there is nothing left to do but go home.
          { path: "accept-invitation", element: <Navigate to="/" replace /> },
          {
            path: "people",
            element: (
              <RequireModule module="core_crm">
                <RequirePermission permission="people.read">
                  <PeoplePage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            path: "people/import",
            element: (
              <RequireModule module="core_crm">
                <RequirePermission permission="people.import">
                  <ImportPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            path: "people/duplicates",
            element: (
              <RequireModule module="core_crm">
                <RequirePermission permission="people.merge" write>
                  <DuplicatesPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            path: "people/tags",
            element: (
              <RequireModule module="core_crm">
                <RequirePermission permission="people.read">
                  <TagsPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            // Households read on people.read or households.manage; the page asks for the wider of the two a staff
            // member is likely to hold, and the policies decide what rows come back.
            path: "people/households",
            element: (
              <RequireModule module="core_crm">
                <RequirePermission permission="people.read">
                  <HouseholdsPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            // Read-only: it reports consent, it never changes it. people.read is enough.
            path: "people/consent",
            element: (
              <RequireModule module="core_crm">
                <RequirePermission permission="people.read">
                  <ConsentPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            path: "website",
            element: (
              <RequireModule module="website">
                <RequirePermission permission="website.read">
                  <WebsitePagesPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            // The studio redesign of the same screen (preview, 2026-10-13).
            path: "website/redesign",
            element: (
              <StudioVariant>
                <RequireModule module="website">
                  <RequirePermission permission="website.read">
                    <WebsitePagesPage />
                  </RequirePermission>
                </RequireModule>
              </StudioVariant>
            ),
          },
          {
            path: "website/menus",
            element: (
              <RequireModule module="website">
                <RequirePermission permission="website.read">
                  <MenusPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            // The studio redesign of the same screen (preview, 2026-10-13).
            path: "website/menus/redesign",
            element: (
              <StudioVariant>
                <RequireModule module="website">
                  <RequirePermission permission="website.read">
                    <MenusPage />
                  </RequirePermission>
                </RequireModule>
              </StudioVariant>
            ),
          },
          {
            // Reading the library needs only website.read; uploading and removing ask for media.manage in the page.
            path: "website/images",
            element: (
              <RequireModule module="website">
                <RequirePermission permission="website.read">
                  <MediaPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            // The studio redesign of the same screen (preview, 2026-10-13).
            path: "website/images/redesign",
            element: (
              <StudioVariant>
                <RequireModule module="website">
                  <RequirePermission permission="website.read">
                    <MediaPage />
                  </RequirePermission>
                </RequireModule>
              </StudioVariant>
            ),
          },
          {
            // Reading the look needs website.read; changing it asks for settings.manage in the page, because branding
            // lives with the church's settings rather than with its pages.
            path: "website/look",
            element: (
              <RequireModule module="website">
                <RequirePermission permission="website.read">
                  <LookPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            // The studio redesign of the same screen (preview, 2026-10-13).
            path: "website/look/redesign",
            element: (
              <StudioVariant>
                <RequireModule module="website">
                  <RequirePermission permission="website.read">
                    <LookPage />
                  </RequirePermission>
                </RequireModule>
              </StudioVariant>
            ),
          },
          {
            // The church's own domain (ADR 0051): the owner's, like the rest of the church's settings.
            path: "website/domain",
            element: (
              <RequireModule module="website">
                <RequirePermission permission="settings.manage">
                  <DomainPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            // The studio redesign of the same screen (preview, 2026-10-13).
            path: "website/domain/redesign",
            element: (
              <StudioVariant>
                <RequireModule module="website">
                  <RequirePermission permission="settings.manage">
                    <DomainPage />
                  </RequirePermission>
                </RequireModule>
              </StudioVariant>
            ),
          },
          {
            // Website → Settings (ADR 0079): offline or online, and the bypass link. website.manage, as the database asks.
            path: "website/settings",
            element: (
              <RequireModule module="website">
                <RequirePermission permission="website.manage">
                  <WebsiteSettingsPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            path: "website/forms",
            element: (
              <RequireModule module="website">
                <RequirePermission permission="forms.manage">
                  <FormsPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            // The studio redesign of the same screen (preview, 2026-10-13).
            path: "website/forms/redesign",
            element: (
              <StudioVariant>
                <RequireModule module="website">
                  <RequirePermission permission="forms.manage">
                    <FormsPage />
                  </RequirePermission>
                </RequireModule>
              </StudioVariant>
            ),
          },
          {
            // Sermons and media (ADR 0061): reading and writing both need sermons.manage, with the module.
            path: "website/sermons",
            element: (
              <RequireModule module="sermons_media">
                <RequirePermission permission="sermons.manage">
                  <SermonsPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            // Devotionals (ADR 0077): devotionals.read opens the list; writing asks for devotionals.manage in the page.
            path: "website/devotionals",
            element: (
              <RequireModule module="devotionals">
                <RequirePermission permission="devotionals.read" module="devotionals">
                  <DevotionalsPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            // The studio redesign of the same screen (preview, 2026-10-13).
            path: "website/sermons/redesign",
            element: (
              <StudioVariant>
                <RequireModule module="sermons_media">
                  <RequirePermission permission="sermons.manage">
                    <SermonsPage />
                  </RequirePermission>
                </RequireModule>
              </StudioVariant>
            ),
          },
          {
            path: "website/forms/:formId",
            element: (
              <RequireModule module="website">
                <RequirePermission permission="forms.manage">
                  <FormEditorPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            // The studio redesign of the same screen (preview, 2026-10-13).
            path: "website/forms/:formId/redesign",
            element: (
              <StudioVariant>
                <RequireModule module="website">
                  <RequirePermission permission="forms.manage">
                    <FormEditorPage />
                  </RequirePermission>
                </RequireModule>
              </StudioVariant>
            ),
          },
          {
            path: "website/pages/:pageId",
            element: (
              <RequireModule module="website">
                <RequirePermission permission="website.read">
                  <PageEditorPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            // The studio redesign of the same screen (preview, 2026-10-13).
            path: "website/pages/:pageId/redesign",
            element: (
              <StudioVariant>
                <RequireModule module="website">
                  <RequirePermission permission="website.read">
                    <PageEditorPage />
                  </RequirePermission>
                </RequireModule>
              </StudioVariant>
            ),
          },
          {
            path: "check-in",
            element: (
              <RequireModule module="event_checkin">
                <RequirePermission permission="events.check_in" module="event_checkin" write>
                  <StationPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            path: "check-in/rooms",
            element: (
              <RequireModule module="event_checkin">
                <RequirePermission permission="events.manage" module="event_checkin" write>
                  <RoomsPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            path: "check-in/doors",
            element: (
              <RequireModule module="event_checkin">
                <RequirePermission permission="events.manage" module="event_checkin" write>
                  <DoorsPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            path: "care",
            element: (
              <RequireModule module="core_crm">
                <RequirePermission permission="care.read" module="core_crm">
                  <CarePage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            path: "store",
            element: (
              <RequireModule module="commerce">
                <RequirePermission permission="commerce.read">
                  <OrdersPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            path: "store/products",
            element: (
              <RequireModule module="commerce">
                <RequirePermission permission="commerce.read">
                  <ProductsPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            path: "store/settings",
            element: (
              <RequireModule module="commerce">
                <RequirePermission permission="commerce.read">
                  <StoreSettingsPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            path: "communications",
            element: (
              <RequirePermission permission="communications.read">
                <AudiencesPage />
              </RequirePermission>
            ),
          },
          {
            path: "communications/templates",
            element: (
              <RequirePermission permission="communications.read">
                <TemplatesPage />
              </RequirePermission>
            ),
          },
          {
            path: "communications/campaigns",
            element: (
              <RequirePermission permission="communications.read">
                <CampaignsPage />
              </RequirePermission>
            ),
          },
          // Email senders (#402): read with communications.read; the card offers changes to communications.send.
          {
            path: "communications/senders",
            element: (
              <RequirePermission permission="communications.read">
                <SendersPage />
              </RequirePermission>
            ),
          },
          // The texting inbox (#314): its own permission, which communications.read alone does not give.
          {
            path: "communications/inbox",
            element: (
              <RequirePermission permission="communications.inbox">
                <InboxPage />
              </RequirePermission>
            ),
          },
          // Text us on the website (ADR 0074): set by those who answer the inbox it feeds.
          {
            path: "communications/text-us",
            element: (
              <RequirePermission permission="communications.inbox">
                <TextUsPage />
              </RequirePermission>
            ),
          },
          {
            path: "membership",
            element: (
              <RequireModule module="membership">
                <RequirePermission permission="membership.read" module="membership">
                  <MembershipPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            path: "services",
            element: (
              <RequireModule module="services">
                <RequirePermission permission="services.read" module="services">
                  <ServicesPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          // Addresses that moved (ADR 0069): the old one, and everything under it, open the same page here.
          ...RENAMED_PATHS.map(({ from, to }) => ({
            path: `${from}/*`,
            element: <RenamedPath from={from} to={to} />,
          })),
          {
            path: "events",
            element: (
              // The module first, so a church without Events is told so rather than "no access" (task 17).
              <RequireModule module="events">
                <RequirePermission permission="events.read" module="events">
                  <EventsPage />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            path: "groups",
            element: (
              <RequireModule module="groups">
                <RequireAnyPermission anyOf={["groups.read", "groups.read_assigned"]}>
                  <GroupsPage />
                </RequireAnyPermission>
              </RequireModule>
            ),
          },
          {
            // Reports (ADR 0052): owners and administrators hold reports.advanced; the module is an add-on.
            path: "reports",
            element: (
              <RequireModule module="advanced_reporting">
                <RequirePermission permission="reports.advanced">
                  <Suspense fallback={<LoadingState rows={4} label="Loading Reports" />}>
                    <ReportsPage />
                  </Suspense>
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            // Owners and administrators: campuses.manage sits in tenant administration, a base module (ADR 0036), so
            // no RequireModule here and no settings.manage Ã¢â‚¬â€ an administrator keeps the church's addresses current.
            path: "campuses",
            element: (
              <RequirePermission permission="campuses.manage">
                <CampusesPage />
              </RequirePermission>
            ),
          },
          {
            path: "settings",
            element: (
              <RequirePermission permission="settings.manage">
                <SettingsPage />
              </RequirePermission>
            ),
          },
          {
            // A church's own sign-in addresses (ADR 0067 phase B, S1): the owner's; every change also needs two-step.
            path: "settings/sign-in-addresses",
            element: (
              <RequirePermission permission="domains.manage_signin">
                <SignInAddressesPage />
              </RequirePermission>
            ),
          },
          {
            path: "support",
            element: (
              <RequirePermission permission="settings.manage">
                <SupportAccessPage />
              </RequirePermission>
            ),
          },
          {
            // Team (ADR 0054; ADR 0036 amendment): reading needs users.read, every change asks users.manage in the page.
            path: "team",
            element: (
              <RequirePermission permission="users.read">
                <TeamPage />
              </RequirePermission>
            ),
          },
          {
            path: "team/roles",
            element: (
              <RequirePermission permission="users.read">
                <RolesPage />
              </RequirePermission>
            ),
          },
          { path: "security", element: <SecurityPage /> },
          {
            path: "*",
            element: (
              <EmptyState
                title="Page not found"
                description="Check the address or use the navigation."
              />
            ),
          },
        ]),
      },
    ],
    basename === "" ? {} : { basename },
  );
}
